import React from "react";
import { Link } from "react-router-dom";
import { Building2, Phone, Mail, Zap, ChevronRight, BarChart3, Shield, Clock } from "lucide-react";

export default function TemplateEnterprise({ site }) {
  const { brand_name, color_primary, color_nav, phone, email, products, headline, tagline } = site;
  const p = color_primary || "#0f172a";
  const n = color_nav || "#0f172a";

  return (
    <div className="min-h-screen bg-slate-100 font-body">
      {/* Top Bar */}
      <div style={{ backgroundColor: n }} className="text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex justify-between">
          <span>24/7 Support {phone && `• ${phone}`}</span>
          <span>Enterprise DTH Management</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 style={{ color: p }} className="w-6 h-6" />
            <span style={{ color: n }} className="font-heading font-bold text-lg">{brand_name || "Enterprise DTH"}</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-600">
            <a href="#plans" className="hover:opacity-70 font-medium">Plans</a>
            <a href="#features" className="hover:opacity-70 font-medium">Features</a>
            <Link to="login" style={{ backgroundColor: n }} className="text-white px-5 py-2 rounded-md text-sm font-semibold hover:opacity-90 inline-block">Portal Login</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ backgroundColor: n }} className="text-white py-16 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span style={{ color: p }} className="text-xs uppercase tracking-[0.2em] font-semibold bg-white/10 px-2 py-1 rounded">Enterprise Grade</span>
            <h1 className="mt-3 font-heading font-bold text-4xl md:text-5xl leading-tight">
              {headline ? headline : <>DTH Management<br />Built for Scale</>}
            </h1>
            <p className="mt-4 text-white/70 text-lg">{tagline || "Reliable service, transparent pricing, and dedicated enterprise support."}</p>
            <button style={{ color: n }} className="mt-6 bg-white px-6 py-3 rounded-md font-bold hover:opacity-90 flex items-center gap-2">
              Get Started <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: BarChart3, label: "99.9% Uptime", sub: "SLA guaranteed" },
              { icon: Shield, label: "Secure", sub: "Bank-grade encryption" },
              { icon: Clock, label: "Instant", sub: "Real-time activation" },
              { icon: Zap, label: "Fast", sub: "Sub-second recharges" },
            ].map(({ icon: Icon, label, sub }, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4">
                <Icon style={{ color: p }} className="w-5 h-5" />
                <p className="font-semibold mt-2">{label}</p>
                <p className="text-xs text-white/50">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 style={{ color: n }} className="font-heading font-bold text-2xl">Subscription Plans</h2>
          <p className="text-slate-500 mt-1">Transparent pricing, no hidden fees</p>
          <div className="mt-8 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3">Plan</th>
                  <th className="text-left px-6 py-3">Operator</th>
                  <th className="text-left px-6 py-3">Validity</th>
                  <th className="text-left px-6 py-3">Channels</th>
                  <th className="text-right px-6 py-3">Price</th>
                  <th className="text-right px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {(products || []).slice(0, 10).map((p_item, i) => (
                  <tr key={p_item.id || i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-6 py-4 font-semibold text-slate-800">{p_item.name}</td>
                    <td className="px-6 py-4 text-slate-600">{p_item.operator}</td>
                    <td className="px-6 py-4 text-slate-600">{p_item.validity_days}d</td>
                    <td className="px-6 py-4 text-slate-600">{p_item.channel_count || "All"}</td>
                    <td className="px-6 py-4 text-right font-heading font-bold" style={{ color: p }}>₹{p_item.sell_price}</td>
                    <td className="px-6 py-4 text-right">
                      <button style={{ backgroundColor: n }} className="text-xs text-white px-3 py-1.5 rounded font-semibold hover:opacity-90">Subscribe</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!products || products.length === 0) && <p className="text-center text-slate-400 py-8">Plans coming soon.</p>}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: n }} className="text-white py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2"><Building2 className="w-5 h-5" /><span className="font-heading font-bold">{brand_name}</span></div>
          <div className="flex gap-4 text-sm text-white/50">
            {phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{phone}</span>}
            {email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{email}</span>}
            <Link to="privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-4 border-t border-white/20 text-center text-xs text-white/50">
          Powered by : Oxiaim Techbreed Pvt. Ltd.
        </div>
      </footer>
    </div>
  );
}
